import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { Constants, PAGE_NAMES } from '../../core/helpers/constants';
import { AppService } from '../../store/app.service';
import { TasksService } from '../../pages/tasks/tasks.service';
import { UtilityService } from '../../core/helpers/utility.service';
import { map, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { InstanceService } from '../../core/instance/instance.service';
import { ReplaySubject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddUserModal } from '../../shared/modals/add-user/add-user.component';
import { UsersApiService } from '../../pages/users/users.apiservice';
import { IUser } from '../../core/models/auth.models';
import { DashboardStyleSettingsModalComponent } from '../../shared/modals/dashboard-style-settings/dashboard-style-settings.component';
import { getActiveUser } from '../../core/helpers/utility';
import { DisplayDensityMode, DisplayDensityService } from '../../shared/services/display-density.service';
import { ThemeMode, ThemeModeService } from '../../shared/services/theme-mode.service';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})

/**
 * Topbar Component
 */
export class TopbarComponent implements OnInit, OnDestroy {
  cookieValue = 'en-US';
  public activePage: string = '';
  searchKeyword = '';
  PAGE_NAMES = PAGE_NAMES;
  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  isAdmin: boolean = false;
  showGoToInstanceBtn: boolean = false;
  showFFM: boolean = false;
  showPRSPR: boolean = false;
  showCPJP: boolean = false;
  externalUser: boolean = false;
  currentUserId: string | null = null;
  currentUserInitials = '??';
  private loadedInitialsUserId: string | null = null;
  themeMode: ThemeMode = 'light';
  densityMode: DisplayDensityMode = 'comfortable';

  titleLogoText: string = 'FFM';
  selectedInstanceText: string = 'FFM';

  @Output() mobileMenuButtonClicked = new EventEmitter<void>();

  constructor(
    private router: Router,
    public _cookiesService: CookieService,
    public translate: TranslateService,
    public appService: AppService,
    private tasksService: TasksService,
    public utilService: UtilityService,
    public instance: InstanceService,
    private modalService: NgbModal,
    private usersApiService: UsersApiService,
    @Inject(DisplayDensityService)
    private displayDensityService: DisplayDensityService,
    @Inject(ThemeModeService)
    private themeModeService: ThemeModeService,
  ) {
    this.syncInstanceBranding();

    this.syncAvailableInstances();

    const userRoles = this.utilService.getUserRoles();
    this.appService.getActiveRoute$()
      .pipe(
        takeUntil(this.destroyed$),
        map((route: any) => {
          const url = route.url;
          if (url.includes('dashboard')) return PAGE_NAMES.Dashboard;
          if (url.includes('clients')) return PAGE_NAMES.Clients;
          if (url.includes('locations')) return PAGE_NAMES.Clients;
          if (url.includes('client-details')) return PAGE_NAMES.ClientDetails;
          if (url.includes('location-details')) return PAGE_NAMES.ClientDetails;
          if (url.includes('prospects')) return PAGE_NAMES.Prospects;
          if (url.includes('archived-locations')) return PAGE_NAMES.Prospects;
          if (url.includes('prospect-details')) return PAGE_NAMES.ProspectsDetails;
          if (url.includes('archived-location-details')) return PAGE_NAMES.ProspectsDetails;
          if (url.includes('lead-details')) return PAGE_NAMES.LeadDetails;
          if (url.includes('rollup-campaigns')) return PAGE_NAMES.Reports;
          if (url.includes('campaigns')) return PAGE_NAMES.Campaigns;
          if (url.includes('campaign-details')) return PAGE_NAMES.CampaignsDetails;
          if (url.includes('edit-campaign')) return PAGE_NAMES.EditCampaign;
          if (url.includes('tags')) return PAGE_NAMES.Tags;
          if (url.includes('global-search')) return PAGE_NAMES.GlobalSearch;
          if (url.includes('task')) return PAGE_NAMES.Tasks;
          if (url.includes('users')) return PAGE_NAMES.Users;
          if (url.includes('companies')) return PAGE_NAMES.Companies;
          if (url.includes('searches')) return PAGE_NAMES.Searches;
          if (url.includes('reports')) return PAGE_NAMES.Reports;
          if (url.includes('leads')) return PAGE_NAMES.Leads;
          if (url.includes('products/')) return PAGE_NAMES.ProductsDetails;
          if (url.includes('products')) return PAGE_NAMES.Products;
          if (url.includes('coupons')) return PAGE_NAMES.Coupons;
          if (url.includes('edit-coupon')) return PAGE_NAMES.EditCoupon;
          if (url.endsWith('subscriptions') || url.endsWith('subscriptions/')) return PAGE_NAMES.Subscriptions;
          if (url.includes('subscriptions')) return PAGE_NAMES.SubscriptionsDetails;
          if (url.includes('emails')) return PAGE_NAMES.Emails;
          if (url.includes('orders/details')) return PAGE_NAMES.OrdersDetails;
          if (url.includes('orders')) return PAGE_NAMES.Orders;
          if (url.includes('shop')) return PAGE_NAMES.Shop;
          // keep MyAccount at the end
          if (url.includes('myaccount')) return PAGE_NAMES.MyAccount;
          return '';
        })
      )
      .subscribe((activePage: string) => {
        this.activePage = activePage;
        this.appService.setActivePage$(this.activePage);
      });

    this.tasksService.onSave.pipe(takeUntil(this.destroyed$)).subscribe((x: boolean) => {
      if (x) {
        this.appService.setForceLoadingTasks(true);
      }
    });

    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => this.syncCurrentUser(user));

    this.themeModeService.watch()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mode) => {
        this.themeMode = mode;
      });

    this.displayDensityService.watch()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mode) => {
        this.densityMode = mode;
      });

    this.isAdmin = userRoles.includes(1);
    
    if (!userRoles.includes(1) && !userRoles.includes(10) && !userRoles.includes(2) && !userRoles.includes(3) && !userRoles.includes(4) && !userRoles.includes(12)) {
      this.externalUser = true;
    }
  }

  private syncInstanceBranding(): void {
    switch (this.instance.getKey()) {
      case 'prspr':
        this.titleLogoText = 'PRSPR';
        this.selectedInstanceText = 'PRSPR';
        break;
      case 'cpjp':
        this.titleLogoText = 'CPJP';
        this.selectedInstanceText = 'CPJP';
        break;
      case 'ffm':
      default:
        this.titleLogoText = 'FFM';
        this.selectedInstanceText = 'FFM';
        break;
    }
  }

  private syncAvailableInstances(): void {
    const ffmUserId = this.getStoredInstanceUserId(Constants.LS_FFM_USER_ID, 'FfmUserId');
    const prsprUserId = this.getStoredInstanceUserId(Constants.LS_PRSPR_USERID, 'PrsprUserId');
    const cpjpUserId = this.getStoredInstanceUserId(Constants.LS_CPJP_USERID, 'CpjpUserId');

    this.showFFM = !!ffmUserId;
    this.showPRSPR = !!prsprUserId;
    this.showCPJP = !!cpjpUserId;

    const availableInstances = [ffmUserId, prsprUserId, cpjpUserId].filter(Boolean).length;
    this.showGoToInstanceBtn = availableInstances > 1;
  }

  private getStoredInstanceUserId(localStorageKey: string, cookieKey: string): string | null {
    const cookieValue = this.normalizeStoredValue(this._cookiesService.get(cookieKey));
    if (cookieValue) {
      return cookieValue;
    }

    return this.normalizeStoredValue(localStorage.getItem(localStorageKey));
  }

  private getStoredAuthToken(): string | null {
    const cookieValue = this.normalizeStoredValue(this._cookiesService.get('AuthToken'));
    if (cookieValue) {
      return cookieValue;
    }

    return this.normalizeStoredValue(localStorage.getItem(Constants.TMP_AUTH_KEY));
  }

  private normalizeStoredValue(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const normalizedValue = value.trim();
    if (!normalizedValue || normalizedValue.toLowerCase() === 'null' || normalizedValue.toLowerCase() === 'undefined') {
      return null;
    }

    return normalizedValue;
  }

  GotoAnotherInstance() {
    const now = new Date();
    now.setHours(now.getHours() + 7);
    localStorage.removeItem(Constants.LS_AUTHENTICATION_KEY);
    const authToken = this.getStoredAuthToken();
    const ffmUserId = this.getStoredInstanceUserId(Constants.LS_FFM_USER_ID, 'FfmUserId');
    const prsprUserId = this.getStoredInstanceUserId(Constants.LS_PRSPR_USERID, 'PrsprUserId');
    const cpjpUserId = this.getStoredInstanceUserId(Constants.LS_CPJP_USERID, 'CpjpUserId');
    const lang = localStorage.getItem(Constants.LS_LANGUAGE) || 'en-US';
    if (authToken) {
      this._cookiesService.set('AuthToken', authToken, {domain: environment.cookieDomain, expires: now, path:'/'});
    }
    if (ffmUserId) {
      this._cookiesService.set('FfmUserId', ffmUserId, {domain: environment.cookieDomain, expires: now, path:'/'});
    }
    if (prsprUserId) {
      this._cookiesService.set('PrsprUserId', prsprUserId, {domain: environment.cookieDomain, expires: now, path:'/'});
    }
    if (cpjpUserId) {
      this._cookiesService.set('CpjpUserId', cpjpUserId, {domain: environment.cookieDomain, expires: now, path:'/'});
    }

    const buildLandingUrl = (base: string) => {
      const url = new URL('landing', base);
      url.searchParams.set('lang', lang);
      return url.href;
    };
    if (this.selectedInstanceText == 'FFM') {
      this._cookiesService.set('CurrentInstance', 'ffm', {domain: environment.cookieDomain, expires: now, path:'/'});
      window.location.href = buildLandingUrl(environment.ffmFrontendUrl);
    } else if (this.selectedInstanceText == 'PRSPR'){
      this._cookiesService.set('CurrentInstance', 'prspr', {domain: environment.cookieDomain, expires: now, path:'/'});
      window.location.href = buildLandingUrl(environment.PrsprFrontendUrl);
    } else if (this.selectedInstanceText == 'CPJP'){
      this._cookiesService.set('CurrentInstance', 'cpjp', {domain: environment.cookieDomain, expires: now, path:'/'});
      window.location.href = buildLandingUrl(environment.cpjpFrontendUrl);
    }
  }

  ngOnInit(): void {
    this.syncInstanceBranding();

    // Language is persisted in localStorage (not cookies) to avoid path/domain scoping issues.
    const lang = localStorage.getItem(Constants.LS_LANGUAGE) || 'en-US';
    this.cookieValue = lang;
    this.translate.use(lang).pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.refreshLanguageLabels();
    });

    const persistedUser = getActiveUser() as IUser | null;
    if (persistedUser) {
      this.syncCurrentUser(persistedUser);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  toggleMobileMenu(event: Event) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Language Value Set
   */
  setLanguage(lang: string) {
    this.cookieValue = lang;
    localStorage.setItem(Constants.LS_LANGUAGE, lang);
    this.translate.use(lang).pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.refreshLanguageLabels();
    });
  }

  openEditUserModal(): void {
    if (!this.currentUserId) {
      return;
    }

    const dialogRef = this.modalService.open(AddUserModal, { size: 'lg' });
    dialogRef.componentInstance.iId = this.currentUserId;

    const dialogSubmitSubscription = dialogRef.componentInstance.oSaveClicked.subscribe((payload: IUser) => {
      this.usersApiService.saveUser(payload)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          dialogRef.dismiss();
          this.appService.fetchAllUsers(null);

          const currentUser = this.appService.getActiveUser$().value;
          if (currentUser?.id === payload.id) {
            this.appService.setActiveUser$({
              ...currentUser,
              ...payload,
            });
          }
        });

      dialogSubmitSubscription.unsubscribe();
    });

    dialogRef.result.finally(() => dialogSubmitSubscription.unsubscribe());
  }

  openDashboardStylesModal(): void {
    this.modalService.open(DashboardStyleSettingsModalComponent, {
      size: 'lg',
      backdrop: false,
    });
  }

  toggleThemeMode(): void {
    this.themeModeService.toggleMode();
  }

  getThemeToggleLabel(): string {
    return this.themeMode === 'light' ? 'DARK_MODE' : 'LIGHT_MODE';
  }

  toggleDensityMode(): void {
    this.displayDensityService.toggleDensity();
  }

  languages = [
    { code: 'en-US', labelKey: 'LANGUAGE_ENGLISH', label: '' },
    { code: 'es', labelKey: 'LANGUAGE_SPANISH', label: '' },
    { code: 'fr', labelKey: 'LANGUAGE_FRENCH', label: '' },
    { code: 'ja', labelKey: 'LANGUAGE_JAPANESE', label: '' },
    { code: 'pt-PT', labelKey: 'LANGUAGE_PORTUGUESE', label: '' },
    { code: 'ko', labelKey: 'LANGUAGE_KOREAN', label: '' }
  ];

  private refreshLanguageLabels(): void {
    this.languages = this.languages.map((lang: any) => ({
      ...lang,
      label: this.translate.instant(lang.labelKey),
    }));
  }

  search() {
    if (this.searchKeyword && this.searchKeyword.trim() !== '')
      this.router.navigate(['pages/global-search/' + this.searchKeyword]);
    this.searchKeyword = '';
  }

  private syncCurrentUser(user: IUser | null | undefined): void {
    const persistedUser = getActiveUser() as IUser | null;
    const resolvedUser = user?.firstName || user?.lastName ? user : persistedUser;

    this.currentUserId = resolvedUser?.id ?? user?.id ?? null;

    const firstName = (resolvedUser?.firstName ?? '').trim();
    const lastName = (resolvedUser?.lastName ?? '').trim();

    const initials = this.buildInitials(firstName, lastName);
    if (initials) {
      this.currentUserInitials = initials;
      return;
    }

    this.loadInitialsFromUserDetails();
    this.currentUserInitials = '??';
  }

  private buildInitials(firstName: string, lastName: string): string {
    const directInitials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase();
    if (directInitials) {
      return directInitials;
    }

    return '';
  }

  private loadInitialsFromUserDetails(): void {
    const userId = (this.currentUserId ?? '').trim();
    if (!userId || this.loadedInitialsUserId === userId) {
      return;
    }

    this.loadedInitialsUserId = userId;
    this.usersApiService.getUserDetails(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          const data = response?.data as IUser | undefined;
          const initials = this.buildInitials(
            (data?.firstName ?? '').trim(),
            (data?.lastName ?? '').trim(),
          );

          if (initials) {
            this.currentUserInitials = initials;
          }
        },
        error: () => {
          this.loadedInitialsUserId = null;
        }
      });
  }
}
